<template>
  <div class="qa-page">
    <div class="qa-container">

      <!-- 뒤로가기 -->
      <button @click="$router.go(-1)" class="qa-back">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        목록
      </button>

      <!-- 로딩 -->
      <div v-if="!notice" class="qa-loading">
        <div class="qa-spinner"></div>
      </div>

      <template v-else>

        <!-- 질문 영역 -->
        <article class="qa-article">
          <div class="qa-badge">Q</div>
          <h1 class="qa-title">{{ notice.title }}</h1>
          <div class="qa-meta">
            <span>{{ notice.author }}</span>
            <span class="qa-meta-dot"></span>
            <span>{{ notice.date }}</span>
          </div>
          <hr class="qa-divider" />
          <div class="qa-content">{{ notice.content }}</div>
        </article>

        <!-- 답변 영역 -->
        <div v-if="adminReply && adminReply.content" class="qa-answer">
          <div class="qa-answer-header">
            <div class="qa-badge qa-badge--a">A</div>
            <div class="qa-answer-info">
              <strong>관리자 답변</strong>
              <time>{{ adminReply.date }}</time>
            </div>
          </div>
          <hr class="qa-divider" />
          <div class="qa-content" v-html="adminReply.content"></div>
        </div>

        <!-- 미답변 -->
        <div v-else-if="!isAdmin" class="qa-empty">
          아직 등록된 답변이 없습니다.
        </div>

        <!-- 관리자 답변 입력 -->
        <div v-if="isAdmin" class="qa-reply-form">
          <div class="qa-reply-form-header">
            <div class="qa-badge qa-badge--a">A</div>
            <strong>답변 작성</strong>
          </div>
          <textarea
            v-model="adminReplyInput"
            rows="6"
            class="qa-textarea"
            placeholder="답변 내용을 입력하세요..."
          ></textarea>
          <div class="qa-reply-form-footer">
            <button @click="submitReply" class="qa-submit-btn" :disabled="!adminReplyInput.trim()">
              답변 등록
            </button>
          </div>
        </div>

      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

const store = useStore();
const route = useRoute();
const user = computed(() => store.state.auth.user);

const notice = ref(null);
const adminReply = ref(null);

async function fetchDetail() {
  const qa_id = route.params.qa_id;
  await store.dispatch('qaBoard/fetchQaDetail', qa_id);
  const detail = store.getters['qaBoard/qaDetail'];

  await store.dispatch('qaBoard/fetchQaComment', qa_id);
  const adminComment = store.getters['qaBoard/qaComment'];

  if (detail) {
    notice.value = {
      title: detail.title,
      author: detail.user_nm,
      date: new Date(detail.creation_date).toISOString().split('T')[0],
      content: detail.content
    };

    adminReply.value = {
      content: adminComment.content,
      date: new Date(adminComment.creation_date).toISOString().split('T')[0]
    };
  }
}

onMounted(fetchDetail);

const isAdmin = computed(() => store.getters['auth/isAdmin']);
const adminReplyInput = ref('');

async function submitReply() {
  if (!adminReplyInput.value.trim()) {
    store.commit('handleNotify', { message: '답변을 입력해주세요.' });
    return;
  }

  try {
    const qa_id = route.params.qa_id;
    await store.dispatch('qaBoard/createQaComment', {
      content: adminReplyInput.value,
      qa_id,
      user_nm: user.value.name,
      user_id: user.value.pid
    });
    store.commit('handleNotify', { message: '답변이 성공적으로 등록되었습니다.', type: 'success' });
    await fetchDetail();
    adminReplyInput.value = '';
  } catch (error) {
    store.commit('handleNotify', { message: '답변 등록에 실패하였습니다.' });
  }
}
</script>

<style scoped>
.qa-page {
  min-height: calc(100vh - 57px);
  background: #f5f5f7;
  padding: 32px 20px 60px;
}
.qa-container {
  max-width: 740px;
  margin: 0 auto;
}

/* 뒤로가기 */
.qa-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #888;
  margin-bottom: 20px;
  transition: color 0.15s;
}
.qa-back:hover { color: #333; }

/* 로딩 */
.qa-loading {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.qa-spinner {
  width: 24px; height: 24px;
  border: 2.5px solid #e0e0e0;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* 뱃지 */
.qa-badge {
  width: 32px; height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
  background: #4f46e5;
  color: #fff;
  flex-shrink: 0;
  letter-spacing: -0.5px;
}
.qa-badge--a {
  background: #7c3aed;
}

/* 질문 카드 */
.qa-article {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02);
}
.qa-title {
  font-size: 22px;
  font-weight: 700;
  color: #111;
  line-height: 1.4;
  margin: 16px 0 12px;
  word-break: break-word;
}
.qa-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #999;
}
.qa-meta-dot {
  width: 3px; height: 3px;
  border-radius: 50%;
  background: #ccc;
}
.qa-divider {
  border: none;
  border-top: 1px solid #f0f0f0;
  margin: 20px 0;
}
.qa-content {
  font-size: 15px;
  line-height: 1.85;
  color: #333;
  white-space: pre-line;
  word-break: break-word;
}

/* 답변 카드 */
.qa-answer {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  margin-top: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02);
  border-left: 4px solid #7c3aed;
}
.qa-answer-header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.qa-answer-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.qa-answer-info strong {
  font-size: 15px;
  font-weight: 600;
  color: #222;
}
.qa-answer-info time {
  font-size: 12px;
  color: #aaa;
}

/* 미답변 */
.qa-empty {
  text-align: center;
  padding: 40px 0;
  font-size: 14px;
  color: #bbb;
}

/* 답변 입력 */
.qa-reply-form {
  background: #fff;
  border-radius: 16px;
  padding: 28px 32px;
  margin-top: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02);
}
.qa-reply-form-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.qa-reply-form-header strong {
  font-size: 15px;
  font-weight: 600;
  color: #222;
}
.qa-textarea {
  width: 100%;
  border: 1.5px solid #e8e8e8;
  border-radius: 12px;
  padding: 16px;
  font-size: 14px;
  line-height: 1.7;
  color: #333;
  resize: vertical;
  min-height: 120px;
  transition: border-color 0.15s;
  outline: none;
  background: #fafafa;
}
.qa-textarea:focus {
  border-color: #7c3aed;
  background: #fff;
}
.qa-textarea::placeholder { color: #bbb; }
.qa-reply-form-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.qa-submit-btn {
  padding: 10px 24px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #111;
  border-radius: 10px;
  transition: all 0.15s;
}
.qa-submit-btn:hover { background: #333; }
.qa-submit-btn:disabled {
  background: #ddd;
  color: #aaa;
  cursor: default;
}
</style>
